import { useMemo, useRef, useState } from 'react'
import {
  AppDialog,
  Select,
  type SelectOption,
} from '../Common'
import { useNls } from '../../nls/nlsProvider'
import {
  rescheduleEduPtmSlot,
  mapEduPtmError,
  type EduPtmSlotItem,
} from '../../services/eduPtmService'
import { generateEduRequestId } from '../../services/eduEventService'
import { utcIsoToZonedParts, DEFAULT_EDU_TIMEZONE } from '../../utils/eduZonedTime'
import './eduPtm.css'

export interface EduPtmRescheduleDialogProps {
  isOpen: boolean
  onClose: () => void
  workspaceId: string
  bookingId: string
  learnerName: string
  slots: EduPtmSlotItem[]
  timezone?: string
  onRescheduled: () => void
}

export const EduPtmRescheduleDialog = ({
  isOpen,
  onClose,
  workspaceId,
  bookingId,
  learnerName,
  slots,
  timezone,
  onRescheduled,
}: EduPtmRescheduleDialogProps) => {
  const { t } = useNls()
  const effectiveTz = timezone || DEFAULT_EDU_TIMEZONE
  const requestIdRef = useRef<string>(generateEduRequestId('ptm_move'))

  const freeSlots = useMemo(() => {
    return slots.filter(
      (s) => !s.isBreak && (!s.booking || s.booking.status === 'CANCELLED'),
    )
  }, [slots])

  const [selectedSlotStart, setSelectedSlotStart] = useState<string>(() => {
    return freeSlots[0]?.slotStart || ''
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const slotOptions = useMemo<SelectOption<string>[]>(() => {
    return freeSlots.map((s) => {
      const startParts = utcIsoToZonedParts(s.slotStart, effectiveTz)
      const endParts = utcIsoToZonedParts(s.slotEnd, effectiveTz)
      return {
        value: s.slotStart,
        label: `${startParts.time} – ${endParts.time}`,
      }
    })
  }, [freeSlots, effectiveTz])

  const handleConfirm = async () => {
    if (!selectedSlotStart) return
    try {
      setIsSubmitting(true)
      setErrorMsg(null)

      await rescheduleEduPtmSlot({
        workspaceId,
        bookingId,
        newSlotStart: selectedSlotStart,
        requestId: requestIdRef.current,
      })

      onRescheduled()
      onClose()
    } catch (err: unknown) {
      setErrorMsg(t(mapEduPtmError(err)))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AppDialog
      open={isOpen}
      onDismiss={onClose}
      title={t('NLSEduPtmRescheduleTitle')}
      size="sm"
      actions={[
        {
          label: t('NLSCommonActionCancel'),
          onClick: onClose,
          variant: 'secondary',
          disabled: isSubmitting,
        },
        {
          label: t('NLSEduPtmRescheduleConfirmButton'),
          onClick: handleConfirm,
          variant: 'primary',
          disabled: isSubmitting || !selectedSlotStart,
        },
      ]}
    >
      <div className="eduPtmEditor">
        {errorMsg && (
          <div className="eduDialog__banner eduDialog__banner--error" role="alert">
            {errorMsg}
          </div>
        )}

        <div className="eduPtmCard__meta">
          <strong>{t('NLSEduPtmLearnerLabel')}</strong> {learnerName}
        </div>

        <div className="eduPtmEditor__field">
          <span className="eduPtmEditor__label">{t('NLSEduPtmSelectNewSlotLabel')}</span>
          {freeSlots.length === 0 ? (
            <p className="eduPtmEditor__help">{t('NLSEduPtmEmptySlots')}</p>
          ) : (
            <Select<string>
              value={selectedSlotStart}
              onChange={(val) => setSelectedSlotStart(val)}
              options={slotOptions}
              disabled={isSubmitting}
              ariaLabel={t('NLSEduPtmSelectNewSlotLabel')}
            />
          )}
        </div>
      </div>
    </AppDialog>
  )
}
